import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../../../components/primitives';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { completeChildSignup } from '../../../lib/parent/completeChildSignup';
import { getFeatures } from '@4cm/core/features';
import './Setup.css';

/**
 * `/parent/child/setup` — completes the loop left open by Wave 3.3.
 *
 * After the parent signs in via the Supabase magic link, they land in
 * the parent shell with no linked children. This screen lets them
 * supply the child's name + email + password, and posts to the
 * `complete-child-signup` edge function which creates the child auth
 * row + parent_children link + sends the child their email-confirmation
 * message.
 *
 * Tone-wise: warm + step-by-step, no Brain personality. Looser
 * spacing inherited from `data-density="rest"` on ParentShell.
 */
export function ChildSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [childName, setChildName] = useState('');
  const [childEmail, setChildEmail] = useState('');
  const [childDob, setChildDob] = useState('');
  const [childPassword, setChildPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bounce unauth visitors to sign-in. Wave 3.3 covers the path that
  // brought them here; this is a defence-in-depth check.
  useEffect(() => {
    if (!user) navigate('/', { replace: true });
  }, [user, navigate]);

  const supabaseUrl = useMemo(
    () => (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '',
    [],
  );

  const passwordTooShort = childPassword.length > 0 && childPassword.length < 10;
  const allFilled =
    childName.trim().length >= 1 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(childEmail) &&
    /^\d{4}-\d{2}-\d{2}$/.test(childDob) &&
    childPassword.length >= 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!allFilled) {
      setError('Please fill every field — password must be at least 10 characters.');
      return;
    }
    setSubmitting(true);
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token ?? '';
    const result = await completeChildSignup({
      supabaseUrl,
      accessToken,
      childName: childName.trim(),
      childEmail: childEmail.trim().toLowerCase(),
      childDob,
      childPassword,
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    // Persist a small flag so /parent shell can fanfare on the new child.
    sessionStorage.setItem('4cm_new_child_id', result.childUserId);
    navigate('/parent', { replace: true });
  };

  return (
    <div className="child-setup">
      <header className="parent-page__head">
        <span className="parent-page__eyebrow">Step 1 — Add your child</span>
        <h1 className="parent-page__title">Set up your child's account</h1>
        <p className="parent-page__lead">
          Your consent is on file. Now we just need a few details so your
          child can sign in. They'll receive an email to confirm their
          address.
        </p>
        {!getFeatures().under13PlayerAccounts && (
          <p className="child-setup__v1-scope">
            Your child's coach uses 4CM to plan training and track development.
            If your child is under 13 they do not have their own login in this
            release — everything you see in your portal is the coach's view,
            shared with you. We'll invite your child to claim their own
            account when we open it up to under-13s.
          </p>
        )}
      </header>

      <Card variant="opaque">
        <form className="child-setup__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
          <label className="child-setup__field">
            <span className="child-setup__label">Child's full name</span>
            <input
              type="text"
              className="child-setup__input"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              autoComplete="off"
              placeholder="Sarah Smith"
              disabled={submitting}
              required
            />
          </label>

          <label className="child-setup__field">
            <span className="child-setup__label">Child's email</span>
            <input
              type="email"
              className="child-setup__input"
              value={childEmail}
              onChange={(e) => setChildEmail(e.target.value)}
              autoComplete="off"
              placeholder="sarah@example.com"
              disabled={submitting}
              required
            />
            <span className="child-setup__hint">
              Use one your child can access — they'll get a confirmation email.
            </span>
          </label>

          <label className="child-setup__field">
            <span className="child-setup__label">Date of birth</span>
            <input
              type="date"
              className="child-setup__input"
              value={childDob}
              onChange={(e) => setChildDob(e.target.value)}
              disabled={submitting}
              required
            />
          </label>

          <label className="child-setup__field">
            <span className="child-setup__label">Starter password</span>
            <input
              type="password"
              className="child-setup__input"
              value={childPassword}
              onChange={(e) => setChildPassword(e.target.value)}
              autoComplete="new-password"
              minLength={10}
              disabled={submitting}
              required
            />
            <span className="child-setup__hint">
              At least 10 characters. Your child can change it after they sign in for the first time.
            </span>
            {passwordTooShort && (
              <span className="child-setup__error-inline">Password is a bit short — needs 10+ characters.</span>
            )}
          </label>

          {error && <p className="child-setup__error">{error}</p>}

          <div className="child-setup__actions">
            <button
              type="submit"
              className="child-setup__btn"
              disabled={!allFilled || submitting}
            >
              {submitting ? 'Setting up…' : "Set up account"}
            </button>
            <Link to="/parent" className="child-setup__btn child-setup__btn--ghost">
              Cancel
            </Link>
          </div>
        </form>
      </Card>

      <p className="parent-page__footnote">
        4CM never shares your child's data with third parties for marketing.
        See our <Link to="/legal/privacy">privacy notice</Link> for the full picture.
      </p>
    </div>
  );
}
